import React, { FC, memo } from "react";
import { Handle, Position, NodeProps } from "@xyflow/react";
import { Box, Typography, Chip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";

const useStyles = makeStyles(() =>
    createStyles({
        node: {
            padding: "12px 16px",
            borderRadius: "8px",
            minWidth: "140px",
            textAlign: "center",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            transition: "all 0.2s ease-in-out",
            cursor: "pointer",
            "&:hover": {
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                transform: "translateY(-2px)",
            },
        },
        startNode: {
            backgroundColor: "#D1FAE5",
            border: "2px solid #10B981",
        },
        storyPartNode: {
            backgroundColor: "#FEF3C7",
            border: "2px solid #F59E0B",
        },
        decisionNode: {
            backgroundColor: "#DBEAFE",
            border: "2px solid #3B82F6",
            borderRadius: "4px",
        },
        endNode: {
            backgroundColor: "#FEE2E2",
            border: "2px solid #EF4444",
        },
        partNumber: {
            backgroundColor: "#121738",
            color: "#ffffff",
            borderRadius: "50%",
            width: "28px",
            height: "28px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: "bold",
            marginBottom: "8px",
        },
        indicators: {
            display: "flex",
            gap: "4px",
            justifyContent: "center",
            marginTop: "8px",
            flexWrap: "wrap",
        },
    }),
);

export interface StoryPartNodeData extends Record<string, unknown> {
    partNumber?: number;
    partLabel: string;
    nodeType: "start" | "story" | "decision" | "end";
    questionCount?: number;
    decisionPointCount?: number;
    isActive?: boolean;
    onClick?: () => void;
}

const StoryPartNode: FC<NodeProps> = ({ data }) => {
    const nodeData = data as StoryPartNodeData;
    const classes = useStyles();

    const getNodeClassName = () => {
        switch (nodeData.nodeType) {
            case "start":
                return classes.startNode;
            case "decision":
                return classes.decisionNode;
            case "end":
                return classes.endNode;
            default:
                return classes.storyPartNode;
        }
    };

    const handleClick = () => {
        if (nodeData.onClick) {
            nodeData.onClick();
        }
    };

    return (
        <Box className={`${classes.node} ${getNodeClassName()}`} onClick={handleClick}>
            {/* Handles for connections */}
            {nodeData.nodeType !== "start" && <Handle type="target" position={Position.Top} />}
            {nodeData.nodeType !== "end" && <Handle type="source" position={Position.Bottom} />}

            <Box>
                {/* Part Number */}
                {nodeData.partNumber && (
                    <Box className={classes.partNumber}>
                        <Typography variant="caption">{nodeData.partNumber}</Typography>
                    </Box>
                )}

                {/* Part Label */}
                <Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: "4px" }}>
                    {nodeData.partLabel}
                </Typography>

                {/* Indicators */}
                {(nodeData.questionCount || nodeData.decisionPointCount) && (
                    <Box className={classes.indicators}>
                        {nodeData.questionCount && nodeData.questionCount > 0 && (
                            <Chip
                                icon={<QuestionAnswerIcon style={{ fontSize: "14px" }} />}
                                label={nodeData.questionCount}
                                size="small"
                                style={{ height: "20px", fontSize: "11px" }}
                            />
                        )}
                        {nodeData.decisionPointCount && nodeData.decisionPointCount > 0 && (
                            <Chip
                                icon={<AccountTreeIcon style={{ fontSize: "14px" }} />}
                                label={nodeData.decisionPointCount}
                                size="small"
                                color="primary"
                                style={{ height: "20px", fontSize: "11px" }}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// Display name for debugging
StoryPartNode.displayName = "StoryPartNode";

// Memoize to prevent unnecessary re-renders
export default memo(StoryPartNode);
